import { Helmet } from "react-helmet-async";

interface Props {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    url?: string;
    type?: string;
    publishedTime?: string;
    modifiedTime?: string;
    language?: string;
    alternateUrl?: string;
    defaultUrl?: string;
    schema?: string;
}

const BASE_URL = "https://www.daisykirbukas.ee";

export function SEO({
    title,
    description = "Daisy Kirbukas – kaasaegne ja omanäoline kirbuturg Tallinnas. Kvaliteetsed ja stiilsed second-hand riided, vintage mood ja preloved esemed aadressil Tööstuse 73, Tallinn. Tule avasta parim kirpputori Tallinnas.",
    keywords = "kirbukas, kirbuturg, kaltsukas, kirpputori, kirppis, second-hand Tallinn, preloved, kasutatud riided, vintage mood, thrift store",
    image = "/logo.png",
    url = "",
    type = "website",
    publishedTime,
    modifiedTime,
    language = "et",
    alternateUrl,
    defaultUrl,
    schema,
}: Props) {
    const brandName = "Daisy Kirbukas";
    const homeTitle = "Daisy Kirbukas - Tallinna glamuurseim kirbuturg";
    let fullTitle = "";
    if (!title || title === "Avaleht") {
        fullTitle = homeTitle;
    } else {
        fullTitle = `${title} | ${brandName}`;
    }
    const fullUrl = `${BASE_URL}${url}`;
    const fullImage = image.startsWith("http") ? image : `${BASE_URL}${image}`;
    const locale = language === "fi" ? "fi_FI" : "et_EE";
    const alternateLang = language === "fi" ? "et" : "fi";

    const shopSchema = {
        "@context": "https://schema.org",
        "@type": "ClothingStore",
        name: "Daisy Kirbukas",
        description:
            "Kaasaegne kirbuturg ja kirpputori Tallinnas. Second-hand riided, vintage mood ja preloved esemed.",
        url: BASE_URL,
        telephone: "+37258104628",
        email: "daisykirbukas@gmail.com",
        sameAs: ["https://instagram.com/daisykirbukas", "https://www.facebook.com/JarjejuttBarbiElu"],
        address: {
            "@type": "PostalAddress",
            streetAddress: "Tööstuse 73",
            addressLocality: "Tallinn",
            addressRegion: "Harjumaa",
            postalCode: "10416",
            addressCountry: "EE",
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: 59.45268,
            longitude: 24.716945,
        },
        openingHoursSpecification: [
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Tuesday", "Wednesday", "Thursday", "Friday"],
                opens: "11:00",
                closes: "19:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Saturday"],
                opens: "11:00",
                closes: "16:00",
            },
        ],
        priceRange: "€-€€",
        paymentAccepted: ["Cash", "Credit Card", "Bank Transfer"],
        currenciesAccepted: "EUR",
        image: fullImage,
        hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Second Hand Clothing",
            itemListElement: [
                {
                    "@type": "Offer",
                    itemOffered: {
                        "@type": "Product",
                        name: "Naiste riided",
                        category: "Second Hand Fashion",
                        image: "https://daisykirbukas.ee/logo.png",
                        offers: {
                            "@type": "Offer",
                            price: "0",
                            priceCurrency: "EUR",
                            availability: "https://schema.org/InStock",
                            url: "https://daisykirbukas.ee",
                        },
                    },
                },
                {
                    "@type": "Offer",
                    itemOffered: {
                        "@type": "Product",
                        name: "Meeste riided",
                        category: "Second Hand Fashion",
                        image: "https://daisykirbukas.ee/logo.png",
                        offers: {
                            "@type": "Offer",
                            price: "0",
                            priceCurrency: "EUR",
                            availability: "https://schema.org/InStock",
                            url: "https://daisykirbukas.ee",
                        },
                    },
                },
                {
                    "@type": "Offer",
                    itemOffered: {
                        "@type": "Product",
                        name: "Aksessuaarid",
                        category: "Second Hand Fashion",
                        image: "https://daisykirbukas.ee/logo.png",
                        offers: {
                            "@type": "Offer",
                            price: "0",
                            priceCurrency: "EUR",
                            availability: "https://schema.org/InStock",
                            url: "https://daisykirbukas.ee",
                        },
                    },
                },
            ],
        },
    };

    const schemas: string[] = [];
    if (schema) {
        schemas.push(schema);
    } else {
        schemas.push(JSON.stringify(shopSchema));
    }

    const breadcrumbElements = [
        {
            "@type": "ListItem",
            position: 1,
            name: "Avaleht",
            item: BASE_URL,
        },
    ];
    if (url !== "/") {
        const pathParts = url.split("/").filter(Boolean);
        let cumulativePath = "";

        pathParts.forEach((part, index) => {
            cumulativePath += `/${part}`;

            const isLast = index === pathParts.length - 1;
            const displayName =
                isLast && title ? title : part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ");

            breadcrumbElements.push({
                "@type": "ListItem",
                position: index + 2,
                name: displayName,
                item: `${BASE_URL}${cumulativePath}`,
            });
        });
    }
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbElements,
    };
    schemas.push(JSON.stringify(breadcrumbSchema));

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={fullUrl} />
            <html lang={language} />

            <link rel="alternate" hrefLang={language} href={fullUrl} />
            {alternateUrl && <link rel="alternate" hrefLang={alternateLang} href={alternateUrl} />}
            {defaultUrl && <link rel="alternate" hrefLang="x-default" href={defaultUrl} />}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:site_name" content={homeTitle} />
            <meta property="og:locale" content={locale} />
            <meta property="og:image" content={fullImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={title} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={fullImage} />

            {type === "article" && publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {type === "article" && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

            {schemas.map((schema, index) => (
                <script key={`schema-${index}`} type="application/ld+json">
                    {schema}
                </script>
            ))}
        </Helmet>
    );
}
